/**
 * LineCompExchangeActions — sipariş satırı için ikram (comp) + iade/değişim
 * (exchange) talebi açma butonları + modalları.
 *
 * Admin Siparişler ve Salon Planı (Canlı) ekranlarında ortak kullanılır
 * (DRY — POS TableScreen'deki çalışan akışın admin'e taşınmış hali).
 *
 * RBAC: butonlar RoleGate ile gizli — talep açma yetkisi (COMP.CREATE /
 * VOID.CREATE) olmayan rol (kasiyer/şef) görmez. Açılan talep PENDING kalır;
 * onay "İkram/İade Onayları" ekranından OWNER/MANAGER tarafından verilir.
 * Backend ayrıca her iki ucu (talep + onay) enforce eder — bu sadece UX gating.
 *
 * Yeni ürün hemen eklenir; iade bedeli/stoğu müdür onayında uygulanır
 * (sektör standardı: her iade onaylı).
 */
import {
  ActionIcon, Button, Checkbox, Group, Modal, NumberInput, Select, Stack,
  Text, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconArrowsExchange, IconGift } from "@tabler/icons-react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCompDraftLine, useExchangeLine, useProducts, useRequestLineComp,
} from "../../../shared/api";
import { RoleGate } from "../../../shared/auth";

// Gerçek comp (müdür onaylı) yetkisi olan satır durumları — hazırlanan/giden ürün.
const COMPABLE_STATUSES = ["CONFIRMED", "PREPARING", "READY", "SERVED"];
// Taslak ikram (direkt %100 indirim) yapılabilen durumlar — henüz mutfağa gitmedi.
const DRAFT_STATUSES = ["DRAFT", "OPEN"];

const formatTRY = (minor: number) =>
  `${(minor / 100).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺`;

interface LineLike {
  id: string;
  product_name?: string;
  quantity: number;
}

interface Props {
  orderId: string;
  line: LineLike;
  kitchenStatus: string;
}

export function LineCompExchangeActions({ orderId, line, kitchenStatus }: Props) {
  const qc = useQueryClient();
  const requestComp = useRequestLineComp();
  const exchangeLine = useExchangeLine();
  const compDraft = useCompDraftLine();
  const { data: allProdsData } = useProducts();
  const allProducts: any[] = allProdsData?.results || allProdsData || [];

  // İkram (comp) talep state
  const [compOpen, setCompOpen] = useState(false);
  const [compQty, setCompQty] = useState<number | string>(1);
  const [compReason, setCompReason] = useState("CUSTOMER_DISSATISFACTION");
  const [compRemake, setCompRemake] = useState(false);

  // Ürün değişimi (exchange) state
  const [exOpen, setExOpen] = useState(false);
  const [exNewProduct, setExNewProduct] = useState<string | null>(null);
  const [exReturnQty, setExReturnQty] = useState<number | string>(1);
  const [exOpened, setExOpened] = useState(false);
  const [exReason, setExReason] = useState("CHANGED_MIND");

  const isDraft = DRAFT_STATUSES.includes(kitchenStatus);
  const isCompable = COMPABLE_STATUSES.includes(kitchenStatus);
  // CLOSED/CANCELLED veya bilinmeyen statüde hiçbir aksiyon yok.
  if (!isDraft && !isCompable) return null;

  const label = `${line.quantity}× ${line.product_name ?? ""}`.trim();
  const maxQty = line.quantity;

  // Talep sonrası order + onay listelerini tazele (drawer güncellensin).
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["orders"] });
    qc.invalidateQueries({ queryKey: ["table-detail"] });
    qc.invalidateQueries({ queryKey: ["line-comps"] });
    qc.invalidateQueries({ queryKey: ["line-returns"] });
  };

  return (
    <>
      <Group gap={4} wrap="nowrap">
        {/* TASLAK/OPEN: direkt ikram (=%100 indirim). Henüz mutfağa gitmedi,
            müdür onayı gerekmez — anında ücretsiz. */}
        {isDraft && (
          <RoleGate permission={{ resource: "COMP", action: "CREATE" }}>
            <Tooltip label="İkram et (ücretsiz yap)">
              <ActionIcon
                size="sm"
                variant="subtle"
                color="pink"
                loading={compDraft.isPending}
                onClick={() => {
                  compDraft.mutate(
                    { orderId, lineId: line.id },
                    {
                      onSuccess: () => {
                        notifications.show({
                          title: "İkram edildi",
                          message: `${label} ücretsiz yapıldı`,
                          color: "green",
                        });
                        refresh();
                      },
                      onError: (e: any) =>
                        notifications.show({
                          message: e?.response?.data?.detail || "Hata",
                          color: "red",
                        }),
                    },
                  );
                }}
              >
                <IconGift size={15} />
              </ActionIcon>
            </Tooltip>
          </RoleGate>
        )}
        {/* CONFIRMED+: gerçek comp talebi (müdür onaylı) + iade/değişim. */}
        {isCompable && (
          <>
            <RoleGate permission={{ resource: "COMP", action: "CREATE" }}>
              <Tooltip label="İkram talebi (müşteri beğenmedi)">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="pink"
                  onClick={() => {
                    setCompQty(1);
                    setCompReason("CUSTOMER_DISSATISFACTION");
                    setCompRemake(false);
                    setCompOpen(true);
                  }}
                >
                  <IconGift size={15} />
                </ActionIcon>
              </Tooltip>
            </RoleGate>
            <RoleGate permission={{ resource: "VOID", action: "CREATE" }}>
              <Tooltip label="İade / ürün değişimi">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="indigo"
                  onClick={() => {
                    setExNewProduct(null);
                    setExReturnQty(1);
                    setExOpened(false);
                    setExReason("CHANGED_MIND");
                    setExOpen(true);
                  }}
                >
                  <IconArrowsExchange size={15} />
                </ActionIcon>
              </Tooltip>
            </RoleGate>
          </>
        )}
      </Group>

      {/* ===== İkram (comp) talep modalı ===== */}
      <Modal
        opened={compOpen}
        onClose={() => { if (!requestComp.isPending) setCompOpen(false); }}
        title="İkram Talebi (Müşteri Beğenmedi)"
        centered
        zIndex={400}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            <b>{label}</b> için ikram talebi açılacak. Müdür onayından sonra
            bedelden düşülür (stok COMPLIMENTARY). Reddedilirse etkisiz olur.
          </Text>
          <NumberInput
            label="Kaç porsiyon ikram"
            value={compQty}
            onChange={setCompQty}
            min={1}
            max={maxQty}
            allowDecimal={false}
            description={`Bu satırda ${maxQty} porsiyon var`}
          />
          <Select
            label="Sebep"
            value={compReason}
            onChange={(v) => v && setCompReason(v)}
            comboboxProps={{ withinPortal: true, zIndex: 500 }}
            data={[
              { value: "CUSTOMER_DISSATISFACTION", label: "Müşteri Beğenmedi" },
              { value: "KITCHEN_ERROR", label: "Mutfak Hatası" },
              { value: "LONG_WAIT", label: "Uzun Bekleme" },
              { value: "GOODWILL", label: "Jest / İyi Niyet" },
              { value: "OTHER", label: "Diğer" },
            ]}
          />
          <Checkbox
            label="Yeniden hazırlansın (remake — mutfağa yeni tabak)"
            checked={compRemake}
            onChange={(e) => setCompRemake(e.currentTarget.checked)}
          />
          <Group justify="flex-end" gap="xs">
            <Button
              variant="default"
              onClick={() => setCompOpen(false)}
              disabled={requestComp.isPending}
            >
              Vazgeç
            </Button>
            <Button
              color="pink"
              leftSection={<IconGift size={14} />}
              loading={requestComp.isPending}
              onClick={() => {
                requestComp.mutate(
                  {
                    order_line_id: line.id,
                    comp_quantity: Number(compQty),
                    reason_code: compReason,
                    remake: compRemake,
                  },
                  {
                    onSuccess: () => {
                      notifications.show({
                        title: "İkram talebi gönderildi",
                        message: "Müdür onayı bekleniyor",
                        color: "green",
                      });
                      setCompOpen(false);
                      refresh();
                    },
                    onError: (e: any) =>
                      notifications.show({
                        message: e?.response?.data?.detail || "Hata",
                        color: "red",
                      }),
                  },
                );
              }}
            >
              Talep Gönder
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ===== Ürün değişimi (exchange) modalı ===== */}
      <Modal
        opened={exOpen}
        onClose={() => { if (!exchangeLine.isPending) setExOpen(false); }}
        title="İade / Ürün Değiştir"
        centered
        zIndex={400}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            <b>{label}</b> iade edilip yeni ürün eklenecek. Yeni ürün hemen
            mutfağa gider; iade <b>müdür onayı</b> bekler (açılmamışsa onayda
            stoğa geri döner, açılmışsa kayıp sayılır).
          </Text>
          <Select
            label="Yeni ürün"
            placeholder="Değiştirilecek ürünü seç"
            searchable
            comboboxProps={{ withinPortal: true, zIndex: 500 }}
            data={allProducts.map((p) => ({
              value: p.id,
              label: `${p.name} — ${formatTRY(p.unit_price_minor)}`,
            }))}
            value={exNewProduct}
            onChange={setExNewProduct}
          />
          <NumberInput
            label="İade porsiyonu"
            value={exReturnQty}
            onChange={setExReturnQty}
            min={1}
            max={maxQty}
            allowDecimal={false}
            description={`Bu satırda ${maxQty} porsiyon var`}
          />
          <Checkbox
            label="Ürün açıldı / tüketildi (iade alınamaz → kayıp)"
            checked={exOpened}
            onChange={(e) => setExOpened(e.currentTarget.checked)}
          />
          <Select
            label="Sebep"
            value={exReason}
            onChange={(v) => v && setExReason(v)}
            comboboxProps={{ withinPortal: true, zIndex: 500 }}
            data={[
              { value: "CHANGED_MIND", label: "Fikir Değiştirdi (başka ürün)" },
              { value: "WRONG_ITEM", label: "Yanlış Ürün Geldi" },
              { value: "QUALITY", label: "Kalite / Beğenmedi" },
              { value: "OTHER", label: "Diğer" },
            ]}
          />
          <Group justify="flex-end" gap="xs">
            <Button
              variant="default"
              onClick={() => setExOpen(false)}
              disabled={exchangeLine.isPending}
            >
              Vazgeç
            </Button>
            <Button
              color="indigo"
              leftSection={<IconArrowsExchange size={14} />}
              loading={exchangeLine.isPending}
              disabled={!exNewProduct}
              onClick={() => {
                if (!exNewProduct) return;
                exchangeLine.mutate(
                  {
                    orderId,
                    order_line_id: line.id,
                    new_product_id: exNewProduct,
                    new_quantity: 1,
                    opened: exOpened,
                    reason_code: exReason,
                    return_quantity: Number(exReturnQty),
                  },
                  {
                    onSuccess: () => {
                      notifications.show({
                        title: "Değişim yapıldı",
                        message: "Yeni ürün eklendi — iade müdür onayı bekliyor",
                        color: "green",
                      });
                      setExOpen(false);
                      refresh();
                    },
                    onError: (e: any) =>
                      notifications.show({
                        message: e?.response?.data?.detail || "Hata",
                        color: "red",
                      }),
                  },
                );
              }}
            >
              İade / Değiştir
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
